// @ts-nocheck
import _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { makeStyles, Box } from '@material-ui/core';
import {
  useAlert,
  useHistoryState,
} from '../../../../common/hooks';
import { AlertSeverity } from '../../../../common/providers';
import { AppRoute } from '../../../app';
import { EmptyState, LoadingWrapper, Panel } from '../../../../common/components';
import PromotionList from './partials/PromotionList';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PromotionService from '../../services/PromotionService';
import { ProductService } from '../../../products';
import { PromotionPeriod } from '../../types';

const useStyles = makeStyles(() => ({
  root: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
  },
  panel: {
    flex: '1',
    height: '100%',
    paddingTop: '68px',
    paddingLeft: '0',
    paddingRight: '0',
    paddingBottom: '0',
  },
}));

function PromotionsPage() {
  const [filterParams, setFilterParams] = useHistoryState('promotionFilterParams', {
    onlyActive: true,
    withActivePeriods: false,
  });
  const [sortParams, setSortParams] = useHistoryState('promotionSortParams', {
    orderBy: 'id',
    sortOrder: 'desc',
  });
  const [t] = useTranslation();
  const { showAlert } = useAlert();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const history = useHistory();
  const classes = useStyles();

  const { data: products, isLoading: productsLoading } = useQuery(['products'], async () => {
    const { data } = await ProductService.fetchProducts();
    return data?.products;
  }, { refetchOnWindowFocus: false});

  const { data: promotions, isLoading: promotionsLoading, isFetching: promotionsFetching } = useQuery(['promotions', page, { filterParams, sortParams }], async () => {
    const { data } = await PromotionService.fetchPromotions(page, 20, filterParams, sortParams);
    return data;
  }, { keepPreviousData: true });

  const createPromotion = useMutation(
    async ({ promotion }: { promotion: Promotion }) => {
      return PromotionService.createPromotion(promotion);
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['promotions']);
        showAlert({
          title: t('common.success'),
          message: t('promotion.promotionCreateSuccess'),
          severity: AlertSeverity.Success,
        });
      },
      onError: (e: Error) => {
        showAlert({
          title: t('promotion.promotionCreateError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      },
    }
  );

  const handleCreatePromotion = useCallback(
    async (promotion: Promotion) => {
      return await createPromotion.mutateAsync({
        promotion
      });
    },
    [createPromotion]
  );

  const createPromotionPeriod = useMutation(
    async ({ period }: { period: PromotionPeriod }) => {
      return PromotionService.createPromotionPeriod(period.promotionId, period);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['promotionDetails']);
        showAlert({
          title: t('common.success'),
          message: t('promotion.promotionPeriodCreateSuccess'),
          severity: AlertSeverity.Success,
        });
      },
      onError: (e: Error) => {
        showAlert({
          title: t('promotion.promotionPeriodCreateError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      },
    }
  );

  const handleCreatePromotionPeriod = useCallback(
    async (period: PromotionPeriod) => {
      await createPromotionPeriod.mutate({
        period
      });
    },
    [createPromotionPeriod]
  );

  const handleSelectPromotion = useCallback(
    params => {
      history.push(AppRoute.PromotionDetails.replace(':promotionId', params.row.id), {
        from: history.location,
      });
    },
    [history]
  );

  const handleFilterChange = useCallback(
    params => {
      setFilterParams({
        ...filterParams,
        ...params,
      });
    },
    [filterParams, setFilterParams]
  );

  const handleSortChange = useCallback(
    params => {
      setSortParams({
        ...sortParams,
        ...params,
      });
    },
    [sortParams, setSortParams]
  );

  const handlePageChange = page => {
    setPage(page);
  };

  const getTypeTranslation = value => {
    switch(value) {
      case 'SALE':
        return t('promotion.promotionTypeSale');
      case 'SALE_PERSISTANCE':
        return t('promotion.promotionTypeSalePersistance');
      case 'CHANGE':
        return t('promotion.promotionTypeChange');
      case 'EVENT':
        return t('promotion.promotionTypeEventDisplay');
    }
  }
  
  return (
    <Box id="Promotions-container" className={classes.root}>
      <LoadingWrapper loading={productsLoading || promotionsLoading}>
        <Panel id="Promotions-panel" title={t('promotion.promotions')} className={classes.panel}>
          {!promotions ? (
            <EmptyState variant="unavailable" message={t('promotion.promotionsNotAvailable')} />
          ) : (
            <PromotionList
              data={promotions}
              products={products}
              onCreatePromotion={handleCreatePromotion}
              onCreatePromotionPeriod={handleCreatePromotionPeriod}
              onSelectPromotion={handleSelectPromotion}
              onPageChange={handlePageChange}
              page={page}
              actionsDisabled={productsLoading || promotionsLoading || promotionsFetching || createPromotion.isLoading}
              onFilterChange={handleFilterChange}
              onSortChange={handleSortChange}
              filterParams={filterParams}
              sortParams={sortParams}
              getTypeDisplayValue={getTypeTranslation}
            />
          )}
        </Panel>
      </LoadingWrapper>
    </Box>
  );
}

export default PromotionsPage;
